import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Download, CheckCircle, Lock, Loader2, Mail, Phone, Building, User } from 'lucide-react';
import { toast } from 'sonner';
import { pdf } from '@react-pdf/renderer';
import IAMReport from './IAMReport';

const DIM_COLORS = {
  gouvernance: '#A855F7',
  securite:    '#EF4444',
  efficacite:  '#06B6D4',
  strategie:   '#F59E0B',
};

const DIM_LABELS = {
  gouvernance: 'Gouvernance',
  securite:    'Sécurité',
  efficacite:  'Efficacité',
  strategie:   'Stratégie',
};

function DimBar({ label, score, color }) {
  return (
    <div className="mb-3">
      <div className="flex justify-between mb-1">
        <span className="text-xs font-medium text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">{score}/25</span>
      </div>
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${(score / 25) * 100}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

export default function StepResults({ iamScore, maturity, answers, onClose }) {
  const { total, gouvernance, securite, efficacite, strategie } = iamScore;
  const [formData, setFormData] = useState({ email: '', firstName: '', lastName: '', company: '', phone: '', wantCallback: false });
  const [submitted, setSubmitted] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Gauge SVG params
  const r = 80;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(total / 100, 1);
  const offset = circ - pct * circ;

  const scoreHex = {
    'text-red-400':    '#EF4444',
    'text-orange-400': '#F97316',
    'text-blue-400':   '#3B82F6',
    'text-green-400':  '#10B981',
  }[maturity.color] || '#A855F7';

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleDownloadPDF = async () => {
    if (!formData.email) {
      toast.error('Veuillez saisir votre email pour recevoir le rapport');
      return;
    }

    setGenerating(true);
    try {
      const reportData = { clientInfo: formData, iamScore, maturity, answers };
      const blob = await pdf(<IAMReport reportData={reportData} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Rapport-Maturite-IAM-${formData.company || formData.firstName || 'Diagnostic'}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      setSubmitted(true);
      toast.success('Rapport téléchargé ! Notre équipe vous contactera sous 24h.');
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">

      {/* Header */}
      <div className="text-center space-y-2">
        <CheckCircle className="w-14 h-14 text-primary mx-auto mb-2" />
        <h2 className="text-3xl font-bold">
          Votre Score de <span className="text-gradient-purple">Maturité IAM</span>
        </h2>
        <p className="text-muted-foreground text-sm">Diagnostic basé sur vos réponses — Rapport personnalisé prêt à télécharger</p>
      </div>

      {/* Score gauge + maturity */}
      <div className="glass rounded-2xl p-8">
        <div className="flex flex-col md:flex-row items-center gap-8">
          {/* Circular gauge */}
          <div className="relative flex-shrink-0">
            <svg className="w-44 h-44 -rotate-90" viewBox="0 0 200 200">
              <circle cx="100" cy="100" r={r} stroke="hsl(var(--muted))" strokeWidth="14" fill="none" />
              <circle
                cx="100" cy="100" r={r}
                stroke={scoreHex} strokeWidth="14" fill="none"
                strokeDasharray={circ} strokeDashoffset={offset}
                strokeLinecap="round"
                className="transition-all duration-1000 ease-out"
                style={{ filter: `drop-shadow(0 0 8px ${scoreHex})` }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-4xl font-bold" style={{ color: scoreHex }}>{total}</div>
              <div className="text-xs text-muted-foreground">/ 100</div>
            </div>
          </div>

          {/* Level info */}
          <div className="flex-1 space-y-4 text-center md:text-left">
            <div>
              <div className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-2" style={{ backgroundColor: `${scoreHex}20`, color: scoreHex, border: `1px solid ${scoreHex}40` }}>
                Risque {maturity.urgency}
              </div>
              <h3 className="text-xl font-bold mb-1">{maturity.level}</h3>
              <p className="text-sm text-muted-foreground">{maturity.description}</p>
            </div>
            <div className="p-4 rounded-xl bg-muted/20 border border-border/50">
              <p className="text-sm leading-relaxed text-muted-foreground">{maturity.message}</p>
            </div>
          </div>
        </div>

        {/* Dimension bars */}
        <div className="mt-6 pt-6 border-t border-border/50">
          <p className="text-sm font-semibold mb-4">Détail par dimension</p>
          <div className="grid md:grid-cols-2 gap-x-8">
            <DimBar label="Gouvernance IAM" score={gouvernance} color={DIM_COLORS.gouvernance} />
            <DimBar label="Sécurité" score={securite} color={DIM_COLORS.securite} />
            <DimBar label="Efficacité opérationnelle" score={efficacite} color={DIM_COLORS.efficacite} />
            <DimBar label="Maturité stratégique" score={strategie} color={DIM_COLORS.strategie} />
          </div>
        </div>
      </div>

      {/* Lead form + PDF download */}
      <div className="glass rounded-2xl p-8">
        {submitted ? (
          <div className="text-center py-8 space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-bold">Rapport téléchargé avec succès !</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              Notre équipe vous contactera sous 24h pour discuter de votre feuille de route Auth0 et débloquer vos outils gratuits.
            </p>
            <Button onClick={onClose} variant="outline" className="mt-4">Fermer</Button>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Download className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1">Recevez votre rapport complet (7 pages PDF)</h3>
                <p className="text-sm text-muted-foreground">Analyse détaillée, matrice des risques, feuille de route Auth0 et 5 outils gratuits débloqués après votre session expert.</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Email (required) */}
              <div>
                <Label htmlFor="email" className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5" />
                  Email professionnel <span className="text-destructive">*</span>
                </Label>
                <Input id="email" name="email" type="email" placeholder="vous@entreprise.com"
                  value={formData.email} onChange={handleChange} className="bg-card/50" required />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-sm font-medium mb-2 flex items-center gap-2">
                    <User className="w-3.5 h-3.5" /> Prénom
                  </Label>
                  <Input id="firstName" name="firstName" placeholder="Jean"
                    value={formData.firstName} onChange={handleChange} className="bg-card/50" />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-sm font-medium mb-2 flex items-center gap-2">
                    <User className="w-3.5 h-3.5" /> Nom
                  </Label>
                  <Input id="lastName" name="lastName" placeholder="Dupont"
                    value={formData.lastName} onChange={handleChange} className="bg-card/50" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company" className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Building className="w-3.5 h-3.5" /> Entreprise
                  </Label>
                  <Input id="company" name="company" placeholder="Votre entreprise"
                    value={formData.company} onChange={handleChange} className="bg-card/50" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5" /> Téléphone
                  </Label>
                  <Input id="phone" name="phone" type="tel" placeholder="+33 6 12 34 56 78"
                    value={formData.phone} onChange={handleChange} className="bg-card/50" />
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <Checkbox
                  id="callback"
                  checked={formData.wantCallback}
                  onCheckedChange={(c) => setFormData(p => ({ ...p, wantCallback: c }))}
                />
                <Label htmlFor="callback" className="text-sm cursor-pointer">
                  Je souhaite être rappelé par un expert Auth0 pour débloquer mes <span className="text-primary font-semibold">5 outils gratuits</span>
                </Label>
              </div>

              {/* Free tools teaser */}
              <div className="grid grid-cols-1 gap-2 pt-2">
                {['Auth0 Migration Checker', 'Password Hash Converter', 'Bulk Import Template', 'Migration Runbook', 'ROI Calculator'].map(tool => (
                  <div key={tool} className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Lock className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    <span>{tool}</span>
                    <span className="ml-auto text-xs text-primary">Accessible après session expert</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={handleDownloadPDF}
                disabled={generating || !formData.email}
                size="lg"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground glow-purple disabled:opacity-50 mt-2"
              >
                {generating ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Génération du rapport…</>
                ) : (
                  <><Download className="w-5 h-5 mr-2" /> Télécharger mon rapport PDF gratuit</>
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                En téléchargeant ce rapport, vous acceptez d'être recontacté par notre équipe. Aucun spam, désabonnement possible à tout moment.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
